import { DocumentNode, useMutation } from '@apollo/client'
import { DirectUpload } from '@rails/activestorage'
import { useContext, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { Mutation } from './create_mutation'
import { CreateAttachmentMutation } from './__generated__/create_mutation'
import { AttachmentResourceType } from '@/types'

interface Props {
  refetchQueries?: DocumentNode[]
  key: string
  resourceId: string
  resourceType: AttachmentResourceType
}

type Progress = {
  percentage: number
  mutationLoading?: boolean
  mutationStarted?: boolean
  errors?: string
}

export const useAttachmentUpload = ({ refetchQueries, key, resourceId, resourceType }: Props) => {
  const [mutate] = useMutation<CreateAttachmentMutation>(Mutation, { refetchQueries })

  const [progress, setProgress] = useState<Record<string, Progress>>({})

  const { token } = useContext<IAuthContext>(AuthContext)

  const handleProgress = (file: File, event: ProgressEvent<XMLHttpRequestEventTarget>) => {
    setProgress(progress => ({
      ...progress,
      [file.name]: {
        mutationLoading: progress[file.name]?.mutationLoading || false,
        percentage: (event.total / event.loaded) * 100.0
      }
    }))
  }

  const upload = (files: File[]) => {
    files.forEach(file => uploadFile(file))
  }

  const uploadFile = (file: File) => {
    new DirectUpload(
      file,
      import.meta.env.VITE_RIPPLE_DIRECT_UPLOAD_ENDPOINT,
      {
        directUploadWillStoreFileWithXHR: (request: XMLHttpRequest) => {
          request.upload.addEventListener('progress', event => {
            handleProgress(file, event)
          })
        }
      },
      { authorization: `Bearer ${token}` }
    ).create((error, blob) => {
      if (error) {
        console.log(error)
      } else if (blob === undefined) {
        console.log('Blob is unexpectedly undefined')
        throw 'Blob is unexpectedly undefined'
      } else {
        setProgress(progress => ({
          ...progress,
          [file.name]: {
            ...progress[file.name],
            mutationStarted: true,
            mutationLoading: true
          }
        }))
        mutate({
          variables: {
            attributes: {
              key,
              signedId: blob.signed_id,
              resourceId,
              resourceType
            }
          }
        }).then(response => {
          setProgress(progress => ({
            ...progress,
            [file.name]: {
              ...progress[file.name],
              mutationLoading: false,
              errors: response.errors?.[0]?.message || response.data?.createAttachment?.errors?.[0]?.message
            }
          }))
        })
      }
    })
  }

  return {
    upload,
    progress,
  }
}
